// La encuesta de ofertas vivas del portal (PP-71): una sola por portal abierto,
// compartida por el aviso de oferta nueva y el Inicio. Antes de PP-71 el
// Inicio tenia la suya y el aviso vivia adentro; con el aviso en todo el
// portal, dos encuestas pedirian lo mismo a la API cada 10 segundos.
//
// La monta el portal arriba del shell: cambiar de seccion no la remonta, asi
// no vuelve a "cargando" ni pierde lo visto, y la cabecera y la barra lateral
// leen lo mismo que el Inicio (reloj de planta, "Ofertas al día" y contador
// de Viajes). Pregunta solo con `enabled`: mientras la cuenta no tiene
// plantas no hay nada que encuestar.
import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import type { ProviderMultiSiteOffersResponse } from "@ucorp/contracts";
import type { PortalSession } from "../auth/session.js";
import type { MultiSitePort } from "../multi-site/port.js";
import { usePolled, type PolledHandle } from "../multi-site/usePolled.js";
import type { Clock } from "../requests/clock.js";

/** Las ofertas se encuestan como la bandeja: el plazo para responder se mide en minutos. */
export const OFFERS_POLL_MS = 10_000;

export type OffersFeed = PolledHandle<ProviderMultiSiteOffersResponse>;

const OffersFeedContext = createContext<OffersFeed | undefined>(undefined);

interface Props {
  port: MultiSitePort;
  session: PortalSession;
  clock: Clock;
  /** Por defecto encuesta; el portal lo apaga mientras las habilitaciones no llegaron o estan vacias. */
  enabled?: boolean;
  children: ReactNode;
}

export function OffersFeedProvider({ port, session, clock, enabled = true, children }: Props): React.JSX.Element {
  const read = useCallback(() => port.offers(session), [port, session]);
  // Con la pestana oculta tambien: el aviso tiene que sonar aunque el operador este en otra.
  const { state, refresh } = usePolled(read, OFFERS_POLL_MS, clock, true, enabled);
  const feed = useMemo(() => ({ state, refresh }), [state, refresh]);
  return <OffersFeedContext.Provider value={feed}>{children}</OffersFeedContext.Provider>;
}

/** La encuesta del portal. Fuera de OffersFeedProvider es un error de cableado. */
export function useOffersFeed(): OffersFeed {
  const feed = useContext(OffersFeedContext);
  if (feed === undefined) throw new Error("useOffersFeed fuera de OffersFeedProvider");
  return feed;
}
